/* eslint-disable react/jsx-no-target-blank */
import React from 'react';
import PropTypes from 'prop-types';
import _ from 'underscore';
import { Budget } from '@bingads-webui-campaign/campaign-ui-schemas';
import { CampaignType } from '@bingads-webui-campaign-react/shell-ui-common';
import { withDefaultStyles } from '@bingads-webui-react/with-default-styles';
import { QueryParam } from '@bingads-webui-react/scope-service';
import { Form } from '@bingads-webui-react/primitive';
import { ErrorMessages } from '@bingads-webui-react/validation';
import { curlyTemplate } from '@bingads-webui-universal/primitive-utilities';
import { ValidationGroup } from '@bingads-webui-react/primitive-validation';
import { withServerContext } from '@bingads-webui-campaign-react/server-context';
import { MinXandrDailyBudgetRangePerCurrency } from '@bingads-webui-campaign-legacy/common-grid-config/budget/get-budget-range';
import { isXandrCampaign } from '@bingads-webui-campaign-react/constants';
import { BudgetEditorWithLandscape } from './budget-landscape';
import { getBudgetEditorStyle } from '../../styles';
import { BaseEditor, KEYSENTER } from '../base-editor';
import { getBulkEditResponseErrors } from '../../helpers';
import { BudgetAmountEditor, BudgetTypeEditor, BudgetMinRecommend } from '../budget-base-component';

const budgetSchema = {
  ...Budget,
  properties: {
    ...Budget.properties,
    Amount: {
      ...Budget.properties.Amount,
      nullable: false,
      type: 'number',
      maxLength: 15,
    },
  },
};

class CampaignInlineBudgetEditorInternal extends React.Component {
  static propTypes = {
    permissions: PropTypes.shape({
      IsDeprecateAcceleratedBudgetEnabled: PropTypes.bool.isRequired,
      IsDeprecateAcceleratedBudgetOptoutEnabled: PropTypes.bool.isRequired,
    }).isRequired,
    i18n: PropTypes.shape({
      getString: PropTypes.func,
    }).isRequired,
    scope: PropTypes.instanceOf(QueryParam).isRequired,
    serverContext: PropTypes.shape({
      BudgetRange: PropTypes.shape({
        DailyRange: PropTypes.shape({
          FloorValue: PropTypes.number.isRequired,
          CeilingValue: PropTypes.number.isRequired,
        }).isRequired,
      }).isRequired,
      CurrentAccount: PropTypes.shape({ CurrencyCode: PropTypes.string }).isRequired,
    }).isRequired,
    data: PropTypes.shape({
      Amount: PropTypes.number,
      Type: PropTypes.string.isRequired,
    }).isRequired,
    hasBudgetLandscape: PropTypes.bool.isRequired,
    campaignType: PropTypes.number.isRequired,
    budget: PropTypes.shape({
      Type: PropTypes.string.isRequired,
    }).isRequired,
    classes: PropTypes.shape({}).isRequired,
    onSave: PropTypes.func.isRequired,
    onCancel: PropTypes.func.isRequired,
    updateInlineBudget: PropTypes.func.isRequired,
    summaryBarActivityLogger: PropTypes.shape({
      summaryBarBudgetActivity: PropTypes.shape({ create: PropTypes.func }).isRequired,
    }).isRequired,
    campaignSubType: PropTypes.string.isRequired,
  };

  constructor(props) {
    super(props);

    this.activityLogger = props.summaryBarActivityLogger
      && props.summaryBarActivityLogger.summaryBarBudgetActivity.create();

    const { serverContext, campaignSubType } = props;

    const { BudgetRange } = serverContext;

    this.minimumBudget = BudgetRange.DailyRange.FloorValue;
    this.maximumBudget = BudgetRange.DailyRange.CeilingValue;

    if (isXandrCampaign(campaignSubType) && MinXandrDailyBudgetRangePerCurrency[serverContext.CurrentAccount.CurrencyCode]) {
      this.minimumBudget = MinXandrDailyBudgetRangePerCurrency[serverContext.CurrentAccount.CurrencyCode];
    }
  }

  state = {
    isValid: false,
    data: this.props.data,
    serverErrors: [],
  };

  onKeyDown = (e) => {
    if (e && e.which === KEYSENTER) {
      e.preventDefault();
      e.stopPropagation();
      this.onSave();
    }
  };

  onValidate = (result) => {
    if (result.data.Amount !== this.state.data.Amount) {
      this.setState({
        isValid: result.isValid,
        data: { ...this.state.data, Amount: result.data.Amount },
      });
    }
  };

  onSave = () => {
    if (this.state.isValid) {
      const updateBudget = (hasBudgetLandscape = false) => this.props.updateInlineBudget({ budget: this.state.data })
        .then((response) => {
          this.props.onSave(response);
          if (this.activityLogger) {
            if (this.props.data.Amount && this.props.data.Amount >= 0.1 && this.state.data && this.state.data.Amount < ((this.props.data.Amount)) * 0.5.toFixed(2)) {
              this.activityLogger.trace(`Bid${this.state.data.Amount} Less than 50% of current budget${this.props.data.Amount}`);
            }
            this.activityLogger.trace(
              {
                updateStatus: 'successful',
                value: this.state.data,
                component: 'campaign summary bar',
                budgetType: 'inline budget',
                hasBudgetLandscape,
                ...(hasBudgetLandscape ? {
                  ActionName: 'Accept',
                  Channel: 'ClientName:bingadsweb[SummaryBarEditing]',
                  campaignId: this.tableView.campaignId,
                  opportunityId: this.tableView.opportunityId,
                  suggestedBudget: this.tableView.ko_model.rawSuggestedBudget,
                  currency: this.tableView.i18n.currency,
                } : {}),
              },
              'AdInsight.Budget'
            );
          }
        })
        .catch((response) => {
          const serverErrors = getBulkEditResponseErrors(response, this.props.i18n);
          this.setState({ serverErrors });

          // server error is reported to user trace here as well
          if (this.activityLogger) {
            this.activityLogger.trace({
              updateStatus: 'failed',
              serverErrors,
              component: 'campaign summary bar',
              budgetType: 'inline budget',
              hasBudgetLandscape,
            });
          }
        });

      if (this.tableView) {
        if (this.tableView.getSelectedNewBudget() === '') {
          this.props.onCancel();
        } else {
          this.setState({ data: { ...this.state.data, Amount: this.tableView.getSelectedNewBudget() } }, () => updateBudget(true));
        }
      } else {
        updateBudget();
      }
    }
  };

  onSelect = (value) => {
    this.setState({ data: { ...this.state.data, Type: value } });
  };

  setTableView = (tableView) => {
    this.tableView = tableView;

    this.tableView.on('saveCustomValueWithKeyboard', () => {
      this.onSave();
    });
  };

  updateBudgetSchemaRange = () => {
    budgetSchema.properties.Amount.minimum = this.minimumBudget;
    budgetSchema.properties.Amount.maximum = this.maximumBudget;
  };

  errorProcessor = (errors) => {
    const { serverContext, i18n } = this.props;
    const { CurrentAccount } = serverContext;
    const { CurrencyCode } = CurrentAccount;

    const customErrors = {
      range: curlyTemplate(i18n.getString(_TL_('Your budget should be greater than {{accountCurrencyCode}} {{dailyRangeFloorValue}} and less than your daily max. The daily max. is your monthly budget averaged out over the number of days left in the month.')))({
        accountCurrencyCode: CurrencyCode,
        dailyRangeFloorValue: this.minimumBudget.toFixed(2),
      }),
    };
    return _.map(
      errors,
      (oldError, key) => customErrors[key] || oldError
    );
  };

  applyRecommend = () => {
    if (this.activityLogger) {
      this.activityLogger.trace('BCCForBudgetMinimumRecommendation: apply');
    }
    this.setState({
      data: { ...this.state.data, Amount: (this.props.data.Amount * 0.5).toFixed(2) },
    });
  };

  renderInlineBudget = (i18n, data, permissions, isDisabled, showBudgetMinRecommend) => (
    <div className="inline-budget-editor form-group">
      <BudgetAmountEditor i18n={i18n} errorProcessor={this.errorProcessor} showPerDay={false} onKeyDown={this.onKeyDown} helpId="pop_BA_CampaignCreate_Budget_SharedBudget" />
      {showBudgetMinRecommend ? <BudgetMinRecommend i18n={i18n} minBudget={(this.props.data.Amount * 0.5).toFixed(2)} applyRecommend={this.applyRecommend} /> : null}
      <BudgetTypeEditor i18n={i18n} permissions={permissions} selectedBudgetType={data.Type} isDisabled={isDisabled} onSelect={this.onSelect} />
    </div>);

  renderInlineBudgetWithLandscape = (i18n, data, permissions, isDisabled, scope) => (
    <div className="budget-editor-with-landscape form-group">
      <BudgetEditorWithLandscape budgetAmount={data.Amount} setTableView={this.setTableView} activityLogger={this.activityLogger} scope={scope} i18n={i18n} />
      <BudgetTypeEditor i18n={i18n} permissions={permissions} selectedBudgetType={data.Type} isDisabled={isDisabled} onSelect={this.onSelect} />
    </div>);

  render() {
    const {
      i18n, classes, onCancel, campaignType, permissions, hasBudgetLandscape, scope, campaignSubType,
    } = this.props;
    const {
      data,
      serverErrors,
      isValid,
    } = this.state;
    const isDisabled = campaignType === CampaignType.Smart;
    this.showBudgetLandscape = hasBudgetLandscape && permissions.IsBudgetInlineSuggestionEnabled;

    this.updateBudgetSchemaRange(budgetSchema);
    this.showBudgetMinRecommend = permissions.dynamic && permissions.dynamic.BCCBudgetImprovementEnabled && this.props.data.Amount && this.props.data.Amount >= 0.1 && data.Amount && (data.Amount < (this.props.data.Amount * 0.5).toFixed(2)) && !isXandrCampaign(campaignSubType);

    if (this.activityLogger && this.showBudgetMinRecommend) {
      this.activityLogger.trace('BCCForBudgetMinimumRecommendation: show');
    }

    return (
      <BaseEditor onSave={this.onSave} onCancel={onCancel} isValid={isValid} isLoading={false}>
        <ValidationGroup validationProps={{
          schema: budgetSchema,
          onValidate: this.onValidate,
          data,
        }}
        >
          <Form className={classes.form}>
            {this.showBudgetLandscape ? this.renderInlineBudgetWithLandscape(i18n, data, permissions, isDisabled, scope) : this.renderInlineBudget(i18n, data, permissions, isDisabled, this.showBudgetMinRecommend)}
            <ErrorMessages messages={_.pluck(serverErrors, 'message')} />
          </Form>
        </ValidationGroup>
      </BaseEditor>
    );
  }
}

const wrappers = [
  withServerContext,
  _.partial(withDefaultStyles, _, getBudgetEditorStyle),
];

const Wrapped = _.compose(...wrappers)(CampaignInlineBudgetEditorInternal);

export { Wrapped as CampaignInlineBudgetEditor };
